import { createAction, createSlice } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';

import type { JobTitle } from './../lib/types/jobTitle';
import { jobTitleApi } from './../lib/api';
import type { ListState, AppState } from './redux';
import { defaultPageSize } from './constants';
import type { FetchListParams } from './../lib/api';
import type { JobTitlesFilter } from '../lib/types/filters';

export type { JobTitlesFilter } from '../lib/types/filters';

export const FETCH_JOB_TITLES = 'vehicles/jobTitles/fetch';
export const SET_JOB_TITLES_FILTERS = 'vehicles/jobTitles/filters/set';
export const CLEAN_JOB_TITLES_FILTERS = 'vehicles/jobTitles/filters/clean';

export type JobTitlesState = ListState<JobTitle> & {
  filters: JobTitlesFilter
};

type JobTitlesThunkDispatch = Dispatch & {
  <Result>(
    thunk: (dispatch: Dispatch, getState: () => AppState) => Result
  ): Result;
};

const initialState: JobTitlesState = {
  data: [],
  page: 1,
  pageSize: defaultPageSize,
  totalCount: 0,
  filters: {}
};

const fetchJobTitlesAction = createAction<{
  data: JobTitle[];
  totalCount: number;
  page: number;
  pageSize: number;
}>(FETCH_JOB_TITLES);
const setJobTitlesFiltersAction = createAction<JobTitlesFilter>(
  SET_JOB_TITLES_FILTERS
);
const cleanJobTitlesFiltersAction = createAction(CLEAN_JOB_TITLES_FILTERS);

const jobTitlesSlice = createSlice({
  name: 'jobTitles',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchJobTitlesAction, (state, { payload }) => ({
        ...state,
        ...payload
      }))
      .addCase(setJobTitlesFiltersAction, (state, { payload }) => ({
        ...state,
        filters: payload
      }))
      .addCase(cleanJobTitlesFiltersAction, state => ({
        ...state,
        filters: {}
      }));
  }
});

export const jobTitlesActions = {
  fetch: fetchJobTitlesAction,
  setFilters: setJobTitlesFiltersAction,
  cleanFilters: cleanJobTitlesFiltersAction
};

export const applyFilters = (filters: JobTitlesFilter) => async (
  dispatch: JobTitlesThunkDispatch
) => {
  dispatch(jobTitlesActions.setFilters(filters));
  dispatch(fetchJobTitles());
};

export const cleanFilters = () => async (dispatch: JobTitlesThunkDispatch) => {
  dispatch(jobTitlesActions.cleanFilters());
  dispatch(fetchJobTitles());
};

export const fetchJobTitles = (
  page: number = 1,
  params: FetchListParams<JobTitlesFilter> = {}
) => async (
  dispatch: Dispatch,
  getState: () => AppState
): Promise<void> => {
  const { filters, pageSize } = getState().jobTitles;
  const { data, totalCount } = await jobTitleApi.fetchJobTitles({
    ...filters,
    page,
    pageSize,
    ...params
  });
  dispatch(
    jobTitlesActions.fetch({
      data,
      totalCount,
      page,
      pageSize
    })
  );
};

export default jobTitlesSlice.reducer;
